/**
 * Admin Portal → Partners → a partner → Statements: the partner's statements, a manual
 * draft for a month, a ledger adjustment, and the ledger itself (entries billed or not).
 * Spec: docs/handoff/PARTNERS_ADMIN_FE_HANDOFF.md §2.5 (Adjustment) and §5 (ledger).
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnerApiError, PartnersAdmin } from '../../../services/partnersAdminService';
import {
  adjustmentBody,
  confirmedAdjustmentBody,
  formatCents,
  isOverdue,
  isoDay,
  ledgerTypeLabel,
  planLabel,
  newIdempotencyKey,
  priorMonth,
  statusBadge,
  type LedgerEntry,
  type StatementRow,
  type TagRow,
} from './partnersLogic';
import {
  NoticeLine,
  StatusBadge,
  errText,
  fmtDate,
  hint,
  input,
  label,
  row,
  td,
  th,
  useBusy,
  type Notice,
} from './PartnersShared';

const LEDGER_PAGE = 100;

export const PartnerStatementsPanel: React.FC<{ partnerId: string; tags: TagRow[]; writable: boolean }> = ({ partnerId, tags, writable }) => {
  const [statements, setStatements] = useState<StatementRow[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const pm = priorMonth(new Date());
  const [draftYm, setDraftYm] = useState(`${pm.year}-${String(pm.month).padStart(2, '0')}`);
  const [showAdj, setShowAdj] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await PartnersAdmin.listStatements(partnerId);
      setStatements(s.statements || []);
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    }
  }, [partnerId]);

  const loadLedger = useCallback(async () => {
    try {
      const l = await PartnersAdmin.ledger(partnerId, { unbilled: unbilledOnly, limit: LEDGER_PAGE, offset: ledgerPage * LEDGER_PAGE });
      setLedger(l.entries || []);
      setLedgerTotal(l.total || 0);
    } catch (e) {
      setNotice({ kind: 'error', text: `Ledger: ${errText(e)}` });
    }
  }, [partnerId, unbilledOnly, ledgerPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLedger(); }, [loadLedger]);

  const draft = () =>
    run(async () => {
      const m = /^(\d{4})-(\d{2})$/.exec(draftYm);
      if (!m) { setNotice({ kind: 'error', text: 'Pick a month.' }); return; }
      try {
        const res = await PartnersAdmin.draft({ year: Number(m[1]), month: Number(m[2]), partner_id: partnerId });
        const n = res.drafted?.length ?? 0;
        setNotice({ kind: n ? 'ok' : 'info', text: n ? `Drafted ${res.drafted.map((d) => d.number).join(', ')}.` : 'Nothing to draft for that month (no unbilled entries, or it is already drafted).' });
        await Promise.all([load(), loadLedger()]);
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const today = isoDay(new Date());

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <AdminCard flush>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Number</th><th style={th}>Period</th><th style={th}>Status</th><th style={th}>Total</th>
              <th style={th}>Due</th><th style={th}>Paid reference</th>
            </tr>
          </thead>
          <tbody>
            {statements.length === 0 && <tr><td style={td} colSpan={6}>No statements yet. The cron drafts the prior month on the 1st.</td></tr>}
            {statements.map((s) => (
              <tr key={s.id}>
                <td style={td}><Link to={`/admin/partners/statements/${s.id}`}>{s.number}</Link></td>
                <td style={td}>{fmtDate(s.period_start)} – {fmtDate(s.period_end)}</td>
                <td style={td}><StatusBadge status={s.status} tone={statusBadge(s.status)} /></td>
                <td style={td}>{formatCents(s.total_cents, s.currency)}</td>
                <td style={{ ...td, color: isOverdue(s, today) ? 'var(--status-error-text, #991b1b)' : undefined }}>
                  {fmtDate(s.due_date)}{isOverdue(s, today) ? ' (overdue)' : ''}
                </td>
                <td style={td}>{s.paid_reference ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminCard>

      {writable && (
        <AdminCard>
          <div style={row}>
            <div>
              <label style={label} htmlFor="dr-month">Draft a month for this partner</label>
              <input id="dr-month" type="month" style={input} value={draftYm} onChange={(e) => setDraftYm(e.target.value)} />
            </div>
            <AdminButton size="sm" variant="secondary" onClick={draft} disabled={busy}>Draft</AdminButton>
            <div style={{ flex: 1 }} />
            <AdminButton size="sm" variant="secondary" onClick={() => setShowAdj((v) => !v)}>{showAdj ? 'Close adjustment' : 'Ledger adjustment'}</AdminButton>
          </div>
          {showAdj && (
            <AdjustmentForm partnerId={partnerId} tags={tags} onWritten={() => { loadLedger(); load(); }} />
          )}
        </AdminCard>
      )}

      <AdminCard>
        <div style={{ ...row, justifyContent: 'space-between', marginBottom: 8 }}>
          <h4 style={{ margin: 0, fontSize: 14 }}>Ledger</h4>
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={unbilledOnly} onChange={(e) => { setLedgerPage(0); setUnbilledOnly(e.target.checked); }} /> Not yet on a statement only
          </label>
        </div>
        <div style={hint}>
          Every accrual, reversal, payment and adjustment, newest first. The ledger is append-only: a correction is a new adjustment, never an edit.
          Lines are anonymized — "user" only says whether the entry belongs to someone.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr>
                <th style={th}>When</th><th style={th}>Type</th><th style={th}>Tag</th><th style={th}>Plan</th><th style={th}>Amount</th>
                <th style={th}>User</th><th style={th}>Statement</th><th style={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {ledger.length === 0 && <tr><td style={td} colSpan={8}>No entries.</td></tr>}
              {ledger.map((l) => (
                <tr key={l.id}>
                  <td style={td}>{fmtDate(l.occurred_at)}</td>
                  <td style={td}>{ledgerTypeLabel(l.entry_type)}</td>
                  <td style={td}>{l.tag ?? '—'}</td>
                  <td style={td}>{planLabel(l.billing_period)}</td>
                  <td style={td}>{formatCents(l.amount_cents, l.currency)}</td>
                  <td style={td}>{l.has_user ? 'yes' : '—'}</td>
                  <td style={td}>
                    {l.statement_id ? (
                      <Link to={`/admin/partners/statements/${l.statement_id}`}>{l.statement_number}</Link>
                    ) : 'not yet billed'}
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>{l.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {ledgerTotal > LEDGER_PAGE && (
          <div style={{ ...row, marginTop: 8 }}>
            <AdminButton size="sm" variant="secondary" disabled={ledgerPage === 0} onClick={() => setLedgerPage((p) => p - 1)}>Previous</AdminButton>
            <span style={hint}>Page {ledgerPage + 1} of {Math.ceil(ledgerTotal / LEDGER_PAGE)}</span>
            <AdminButton size="sm" variant="secondary" disabled={(ledgerPage + 1) * LEDGER_PAGE >= ledgerTotal} onClick={() => setLedgerPage((p) => p + 1)}>Next</AdminButton>
          </div>
        )}
      </AdminCard>
    </div>
  );
};

// ── one ledger adjustment ────────────────────────────────────────────────────

export const AdjustmentForm: React.FC<{ partnerId: string; tags: TagRow[]; onWritten: () => void }> = ({ partnerId, tags, onWritten }) => {
  // ONE key per form, minted when it opens, resent unchanged on every retry — a retry
  // after a lost answer writes nothing twice (the server answers replayed: true).
  const [key, setKey] = useState(() => newIdempotencyKey());
  const [f, setF] = useState({ amount: '', note: '', tagId: '', userId: '' });
  const [notice, setNotice] = useState<Notice>(null);
  // The refused request itself: "Write it anyway" resends EXACTLY this body (same key,
  // same amount, same user) with the confirmation — never a body rebuilt from a form that
  // may have changed since, which would confirm a different adjustment without its own
  // warning (a second user id → a bounty paid twice). Any edit drops it.
  const [pending, setPending] = useState<{ message: string; body: Record<string, unknown> } | null>(null);
  const [busy, run] = useBusy();

  const edit = (k: keyof typeof f, v: string) => {
    setF((cur) => ({ ...cur, [k]: v }));
    setPending(null);
  };

  const send = (body: Record<string, unknown>) =>
    run(async () => {
      try {
        const res = await PartnersAdmin.writeAdjustment(partnerId, body);
        setPending(null);
        setNotice({
          kind: 'ok',
          text: `${res.replayed ? 'Already written (this form was sent before) — nothing new. ' : ''}Adjustment of ${formatCents(res.entry.amount_cents, res.entry.currency)} recorded. ${res.billing}`,
          extra: (res.warnings || []).map((w) => w.message),
        });
        onWritten();
      } catch (e) {
        if (e instanceof PartnerApiError && e.code === 'user_has_no_bounty_entry' && body.confirm_no_bounty_entry !== true) {
          setPending({ message: e.message, body });
          return;
        }
        setPending(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const submit = () => {
    const { body, errors } = adjustmentBody(f, key, false);
    if (errors.length) { setNotice({ kind: 'error', text: 'Not written:', extra: errors }); return; }
    send(body);
  };

  const writeAnyway = () => {
    if (pending) send(confirmedAdjustmentBody(pending.body));
  };

  const fresh = () => {
    setKey(newIdempotencyKey());
    setF({ amount: '', note: '', tagId: '', userId: '' });
    setNotice(null);
    setPending(null);
  };

  return (
    <div style={{ marginTop: 12, padding: 12, background: 'var(--surface-subtle, #f9fafb)', borderRadius: 10 }}>
      <div style={hint}>
        One signed entry, written now: positive = we owe the partner more, negative = a credit back to us. The reason stays internal (statement
        lines carry no notes — set the statement note to explain it to the partner).
      </div>
      <NoticeLine notice={notice} />
      {pending && (
        <div role="alert" style={{ background: 'var(--status-warning-bg, #fffbeb)', color: 'var(--status-warning-text, #92400e)', padding: 10, borderRadius: 8, fontSize: 13, margin: '8px 0' }}>
          {pending.message}
          <div style={{ marginTop: 8 }}>
            <AdminButton size="sm" variant="danger" onClick={writeAnyway} disabled={busy}>Write it anyway</AdminButton>{' '}
            <AdminButton size="sm" variant="ghost" onClick={() => setPending(null)}>Cancel</AdminButton>
          </div>
        </div>
      )}
      <div style={{ ...row, marginTop: 8 }}>
        <div>
          <label style={label} htmlFor="adj-amount">Amount (CAD, signed)</label>
          <input id="adj-amount" style={{ ...input, width: 120 }} value={f.amount} onChange={(e) => edit('amount', e.target.value)} placeholder="-25.00" />
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <label style={label} htmlFor="adj-note">Reason (required, internal)</label>
          <input id="adj-note" style={{ ...input, width: '100%' }} value={f.note} maxLength={500} onChange={(e) => edit('note', e.target.value)} />
        </div>
        <div>
          <label style={label} htmlFor="adj-tag">Tag (optional)</label>
          <select id="adj-tag" style={input} value={f.tagId} onChange={(e) => edit('tagId', e.target.value)}>
            <option value="">None</option>
            {tags.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="adj-user">User id (optional)</label>
          <input id="adj-user" style={{ ...input, width: 290 }} value={f.userId} onChange={(e) => edit('userId', e.target.value)} />
        </div>
        <AdminButton size="sm" onClick={submit} disabled={busy}>Write adjustment</AdminButton>
        <AdminButton size="sm" variant="ghost" onClick={fresh} disabled={busy}>New adjustment</AdminButton>
      </div>
      <div style={hint}>Retrying this form is safe (same key). Use "New adjustment" to write a second, different one.</div>
    </div>
  );
};
